import { FormEvent, useState } from "react";
import { ApiError } from "../../api/client";
import { Vehicle, updateVehicleKm } from "../../api/vehicles";
import { formatPlateDisplay } from "../../utils/plate";
import "./KmUpdateModal.css";

type Props = {
  vehicle: Vehicle;
  onClose: () => void;
  onSaved: (vehicle: Vehicle) => void;
};

export function KmUpdateModal({ vehicle, onClose, onSaved }: Props) {
  const [currentKm, setCurrentKm] = useState<string>(String(vehicle.current_km));
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const km = Number(currentKm);
  const isValid =
    currentKm.length > 0 && Number.isFinite(km) && Number.isInteger(km) && km >= vehicle.current_km;
  const delta = Number.isFinite(km) ? km - vehicle.current_km : 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || !isValid) return;
    setError(null);
    setSubmitting(true);
    try {
      const saved = await updateVehicleKm(vehicle.id, {
        currentKm: km,
        reason: reason.trim() || null,
      });
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "KM Güncellenemedi.");
      } else {
        setError("Sunucuya ulasilamadi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-modal kp-card kp-km-form">
        <div className="kp-modal__head">
          <div>
            <strong>KM Güncelle</strong>
            <span>
              {formatPlateDisplay(vehicle.plate_no)} · {vehicle.brand_model || "-"}
            </span>
          </div>
          <button className="kp-btn kp-btn--ghost" onClick={onClose} disabled={submitting}>
            Kapat
          </button>
        </div>

        <form className="kp-km-form__body" onSubmit={submit}>
          <div className="kp-km-form__current">
            <span>Mevcut KM</span>
            <strong>{vehicle.current_km.toLocaleString("tr-TR")}</strong>
          </div>

          <label className="kp-km-form__field">
            <span>Yeni KM *</span>
            <input
              className="kp-input"
              type="number"
              min={vehicle.current_km}
              required
              autoFocus
              value={currentKm}
              onChange={(e) => setCurrentKm(e.target.value)}
            />
            <small>KM geri alinamaz; yeni deger mevcut KM'ye esit veya buyuk olmalidir.</small>
          </label>

          {Number.isFinite(km) && delta > 0 && (
            <div className="kp-km-form__delta">
              Bu islemle <strong>+{delta.toLocaleString("tr-TR")} km</strong> eklenecek.
            </div>
          )}

          <label className="kp-km-form__field">
            <span>Not / Sebep (opsiyonel)</span>
            <input
              className="kp-input"
              type="text"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ornek: 14:00 dersi sonrasi"
            />
          </label>

          {error && <div className="kp-km-form__error">{error}</div>}

          <div className="kp-km-form__actions">
            <button type="button" className="kp-btn" onClick={onClose} disabled={submitting}>
              İptal
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn--primary"
              disabled={submitting || !isValid}
            >
              {submitting ? "Kaydediliyor…" : "KM'yi Güncelle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
